import { useConfirmMfa, useStartMfa } from '@myrentalmate/api-client';
import { Alert, Box, Button, Stack, TextField, Typography } from '@mui/material';
import { QRCodeSVG } from 'qrcode.react';
import { useState, type FormEvent } from 'react';

import { okData } from '../../lib/apiData';
import { apiErrorMessage } from '../../lib/apiError';

/** Setting up two-factor, from the QR code through to the backup codes. Shared by My profile and
 * the page shown when a business requires it. */
export function TwoFactorSetup({ onDone }: { onDone: () => void }) {
  const start = useStartMfa();
  const confirm = useConfirmMfa();
  const [code, setCode] = useState('');
  const setup = okData(start.data);
  const backupCodes = okData(confirm.data)?.backup_codes;

  if (backupCodes) {
    return (
      <Stack spacing={2} sx={{ alignItems: 'flex-start' }}>
        <Alert severity="success">Two-factor is on.</Alert>
        <Typography>
          <strong>Save these backup codes somewhere safe.</strong> Each works once, if you lose your
          phone. They won't be shown again.
        </Typography>
        <Box
          component="ul"
          aria-label="Backup codes"
          sx={{
            m: 0,
            p: 2,
            listStyle: 'none',
            fontFamily: 'monospace',
            columns: 2,
            bgcolor: 'background.default',
            borderRadius: 1,
          }}
        >
          {backupCodes.map((backup) => (
            <li key={backup}>{backup}</li>
          ))}
        </Box>
        <Button variant="contained" onClick={onDone}>
          I've saved them
        </Button>
      </Stack>
    );
  }

  if (!setup) {
    return (
      <Stack spacing={2} sx={{ alignItems: 'flex-start' }}>
        {start.isError && <Alert severity="error">{apiErrorMessage(start.error)}</Alert>}
        <Button variant="contained" disabled={start.isPending} onClick={() => start.mutate()}>
          Set up two-factor
        </Button>
      </Stack>
    );
  }

  function submit(event: FormEvent) {
    event.preventDefault();
    confirm.mutate({ data: { code } }, { onError: () => setCode('') });
  }

  return (
    <Stack component="form" spacing={2} onSubmit={submit} sx={{ alignItems: 'flex-start' }}>
      <Typography>
        Scan this with an authenticator app, such as Google Authenticator, Microsoft Authenticator
        or 1Password.
      </Typography>
      <Box sx={{ p: 1.5, bgcolor: 'common.white', borderRadius: 1, lineHeight: 0 }}>
        <QRCodeSVG value={setup.otpauth_uri} size={180} title="Two-factor QR code" />
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>
        Can't scan it? Enter this key: <code>{setup.secret}</code>
      </Typography>
      {confirm.isError && <Alert severity="error">{apiErrorMessage(confirm.error)}</Alert>}
      <TextField
        label="6-Digit Code From the App"
        required
        fullWidth
        autoComplete="one-time-code"
        value={code}
        onChange={(e) => setCode(e.target.value)}
      />
      <Button type="submit" variant="contained" disabled={confirm.isPending}>
        {confirm.isPending ? 'Checking…' : 'Turn on two-factor'}
      </Button>
    </Stack>
  );
}
